import type { SchemeReferenceData } from '../scheme-reference/scheme-reference-loader';
import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subscription, defer, finalize, forkJoin, map, switchMap, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { withPdfPages$ } from '../pdf-pages/with-pdf-pages';
import { GroupService } from './group.service';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

/**
 * `data.documentRequests[]` on `GET api/patient-invites/:id` — `tasks/practice-documents.md` §19.
 *
 * One practice document the patient is being asked to complete inside this invite. `id` is the
 * only handle that travels back: the two endpoints below are addressed by invite id + request id
 * and authenticated by the invite's own short token, exactly as the practice-form requests are.
 *
 * THERE IS DELIBERATELY NO DOWNLOAD URL HERE. The blank's blob is stored unsigned and a signed URL
 * is minted per request (§10.1 decision 3); a wizard sits open while a patient walks to a printer,
 * so a URL baked into the load payload would be dead by the time it was clicked. `getDocumentBlankUrl`
 * mints one, and the caller re-mints on the server's own clock.
 */
export interface InviteDocumentUploadLimits {
  maxFiles?: number | null;
  maxTotalBytes?: number | null;
  /** A SENTENCE for a patient, not a MIME list. Printed verbatim, never paraphrased. */
  accepts?: string | null;
}

export interface InviteDocumentRequestPayload {
  id: string;
  name?: string | null;
  description?: string | null;
  pageCount?: number | null;
  /** What the patient's download is called. */
  fileName?: string | null;
  /** The hard block: `true` means the wizard will not advance past this step until it is returned. */
  requiredInInvite?: boolean;
  /**
   * `'embedded'` (tier A — fill it in on screen) or `'download'` (tier C) —
   * `tasks/practice-documents.md` §5. OPTIONAL on this interface because the wizard must survive
   * a payload without it; `readFillMode` in `patient-document-copy.ts` resolves anything that is
   * not literally `'embedded'` to `'download'`, which is the path that works for every document.
   */
  fillMode?: string | null;
  upload?: InviteDocumentUploadLimits | null;

  /**
   * THIS DOCUMENT WAS SENT BACK — `tasks/document-rejection-resend.md`.
   *
   * A rejection re-opens the SAME ask, so a resubmission arrives here as an ordinary outstanding
   * document and is otherwise indistinguishable from a first one. Telling somebody who already
   * filled a form in and photographed every page to "download the document and complete it" reads
   * as though the practice lost it.
   *
   * OPTIONAL, and absent means "an ordinary ask": a server that has not shipped this sends
   * neither field, and the step must render exactly as it always did.
   */
  resubmission?: boolean;
  /** The reviewer's own words about what needs to change. Only ever set on a resubmission. */
  previousNote?: string | null;
  /** How many pages they sent last time, so the step can offer them without a round trip. */
  previousFileCount?: number;
}

/** One page of a previous attempt, as `POST .../documents/:requestId/previous` serves it. */
export interface InviteDocumentPreviousFile {
  id: string;
  fileName: string | null;
  mediaType: string | null;
  /**
   * Short-lived and minted per request. `null` when the stored reference could not be parsed —
   * reported rather than dropped, because a page that exists and cannot be opened is a different
   * fact from a page that was never there.
   */
  url: string | null;
}

/** `POST .../documents/:requestId/previous` → `data`. */
export interface InviteDocumentPrevious {
  files: InviteDocumentPreviousFile[];
  expiresInMinutes: number | null;
}

/** `POST .../documents/:requestId/blank` → `data`. A short-lived signed URL and its lifetime. */
export interface InviteDocumentBlank {
  url: string | null;
  /** How long the URL above is good for. Callers re-mint on THIS clock, not on a guess. */
  expiresInMinutes: number | null;
  fileName: string | null;
  pageCount: number | null;
}

/**
 * `POST .../documents/:requestId/files` → the response body.
 *
 * `alreadyReturned` is a SUCCESS, not an error: the endpoint claims before it works, so a retry
 * over a dropped response replays this rather than filing a second copy of every page.
 */
export interface InviteDocumentUploadResult {
  success?: boolean;
  alreadyReturned?: boolean;
  status?: string;
  message?: string;
  data?: { returnedAt?: string | null; fileCount?: number | null } | null;
}

/** What `uploadDocumentFiles` emits: progress while the bytes move, then exactly one `done`. */
export type InviteDocumentUploadEvent =
  | { kind: 'progress'; percent: number | null }
  | { kind: 'done'; result: InviteDocumentUploadResult };

/** Cooldown state for resend (UTC-based; correct for any user timezone). */
export interface ResendCooldown {
  canResend: boolean;
  secondsRemaining: number;
}

/** Get cooldown from nextAllowedResendAt (ISO UTC string or Date). */
export function getResendCooldown(nextAllowedResendAt: string | Date | null | undefined): ResendCooldown {
  if (!nextAllowedResendAt) {
    return { canResend: true, secondsRemaining: 0 };
  }
  const next = typeof nextAllowedResendAt === 'string' ? new Date(nextAllowedResendAt) : nextAllowedResendAt;
  const now = Date.now();
  const remaining = Math.max(0, Math.ceil((next.getTime() - now) / 1000));
  return {
    canResend: remaining <= 0,
    secondsRemaining: remaining,
  };
}

/** Format seconds as "Xm Ys" or "Xs" when under 60s. */
export function formatCooldownRemaining(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/** Cooldown formula (must match backend): 60s, 120s, 240s, ... cap 3600. */
function getResendCooldownSeconds(resendCount: number): number {
  if (resendCount == null || resendCount < 1) return 0;
  return Math.min(3600, 60 * Math.pow(2, resendCount - 1));
}

/** Compute nextAllowedResendAt (ISO string) from invite with lastResendAt and resendCount (e.g. from socket). */
export function computeNextAllowedResendAt(invite: { lastResendAt?: string | Date | null; resendCount?: number }): string | null {
  const last = invite?.lastResendAt;
  const count = invite?.resendCount ?? 0;
  const sec = getResendCooldownSeconds(count);
  if (!last || sec <= 0) return null;
  const next = new Date(last);
  next.setTime(next.getTime() + sec * 1000);
  return next.toISOString();
}

// Utility function to format date fields in a patient object
function formatInviteDates(patient: any): any {
  if (patient.dob) {
    patient.dob = moment(patient.dob, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY');
  }
  return patient;
}

@Injectable({
  providedIn: 'root'
})
export class PatientInviteService {
  private apiUrl = 'api';
  private groupInvitesSubject = new BehaviorSubject<any[]>([]);
  private patientInvitesSubject = new BehaviorSubject<any[]>([]);
  private patientEmailsSubject = new BehaviorSubject<any[]>([]);


  /**
   * The practice's invites, loaded when the Invites tab first shows them rather than on every practice switch (they
   * are the largest load on a switch). A switch while the tab is open loads the new practice's invites. It is null
   * until they have arrived, so the tab can tell "loading" from "no invites".
   */
  invites$ = defer(() => {
    this.inviteWatchers++;
    this.ensureInvites();
    return this.groupInvitesSubject.pipe(
      map(invites => (this.invitesReady ? invites : null)),
      finalize(() => this.inviteWatchers--),
    );
  });
  private inviteWatchers = 0;
  private invitesFor: string | null = null;
  private invitesReady = false;
  private invitesLoad?: Subscription;
  patientInvites$ = this.patientInvitesSubject.asObservable();
  patientEmails$ = this.patientEmailsSubject.asObservable();

  groupId: any;
  isLoading = false;

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService, private patientService: PatientService) {

    this.setupRealTimeUpdates();
    this.subscribeToCurrentPatient();

    this.groupService.selectedGroup$.subscribe(selectedGroup => {
      if (!selectedGroup) return;
      this.groupId = selectedGroup.id;
      this.invitesReady = false;
      this.groupInvitesSubject.next([]);
      this.invitesLoad?.unsubscribe();
      this.invitesFor = null;
      if (this.inviteWatchers > 0) this.ensureInvites();
    });
  }

  /**
   * The patient app (tasks/patient-app.md §15): the wizard's first screen, "Use the MedicAI app". The practice's whole
   * invite goes to the app; the patient completes it there. The invite token goes in the BODY, never the URL; the answer
   * carries a one-use handoff in its fragment.
   */
  requestAppInviteHandoff(token: string): Observable<{ success: boolean; url: string; expiresAt: string }> {
    return this.http.post<{ success: boolean; url: string; expiresAt: string }>(`${this.apiUrl}/patient-invites/app-invite-handoff`, { token });
  }

  /** The scheme list for the wizard's medical aid picker. Signed out: the invite token, in the body, is the proof. */
  getSchemeReference(token: string): Observable<{ hash: string | null; data?: SchemeReferenceData | null }> {
    return this.http.post<{ hash: string | null; data?: SchemeReferenceData | null }>(`${this.apiUrl}/scheme-reference/invite`, { token });
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientInvitesSubject.next([]);
          this.patientEmailsSubject.next([]);
          return forkJoin([this.loadInvitesByPatient(patient.id), this.loadEmailsByPatient(patient.id)]);
        } else {
          // If there's no current patient, clear the notes
          this.patientInvitesSubject.next([]);
          this.patientEmailsSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap(([invites, emails]: [any, any]) => {
        invites = invites ? invites.data : [];
        this.patientInvitesSubject.next(invites);
        emails = emails ? emails.data : [];
        this.patientEmailsSubject.next(emails);
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });


    // Listen for a new patient being added
    this.socketService.listen('patientInviteAdded', 'patient-invite-service').subscribe((newInvite) => {
      if (!newInvite.consentOnly) {
        const formattedInvite = formatInviteDates(newInvite);
        const currentInvites = this.groupInvitesSubject.getValue();
        this.groupInvitesSubject.next([...currentInvites, formattedInvite]); // Add the new invite
      }
      if (newInvite.patientId === currentPatientId) {
        const current = this.patientInvitesSubject.getValue() || [];
        current.unshift(newInvite);
        this.patientInvitesSubject.next(current);
      }
    });

    // Listen for patient updates
    this.socketService.listen('patientInviteUpdated', 'patient-invite-service').subscribe((updatedInvite) => {
      const nextAt = updatedInvite.nextAllowedResendAt ?? computeNextAllowedResendAt(updatedInvite);
      const inviteWithCooldown = { ...updatedInvite, nextAllowedResendAt: nextAt };
      if (!updatedInvite.consentOnly) {
        const formattedInvite = formatInviteDates(inviteWithCooldown);
        const invites = this.groupInvitesSubject.getValue();
        const index = invites.findIndex(p => p.id === formattedInvite.id);
        if (index !== -1) {
          invites[index] = formattedInvite;
          this.groupInvitesSubject.next([...invites]);
        }
      }
      if (updatedInvite.patientId === currentPatientId) {
        const current = this.patientInvitesSubject.getValue() || [];
        const updated = current!.map(item => item.id === updatedInvite.id ? inviteWithCooldown : item);
        this.patientInvitesSubject.next(updated);
      }
    });

    // Listen for a patient being deleted
    this.socketService.listen('patientInviteDeleted', 'patient-invite-service').subscribe((deletedInvite) => {
      const formattedInvite = formatInviteDates(deletedInvite);
      const currentInvites = this.groupInvitesSubject.getValue();
      const updatedInvites = currentInvites.filter(p => p.id !== formattedInvite.id);
      this.groupInvitesSubject.next(updatedInvites); // Remove the deleted invite
      if (deletedInvite.patientId === currentPatientId) {
        const current = this.patientInvitesSubject.getValue() || [];
        const updated = current!.filter(item => item.id !== deletedInvite.id);
        this.patientInvitesSubject.next(updated);
      }
    });
  }

  private ensureInvites(): void {
    if (!this.groupId || this.invitesFor === this.groupId) return;
    this.invitesFor = this.groupId;
    this.invitesReady = false;
    this.invitesLoad = this.getInvitesByGroup(this.groupId).subscribe((result: any) => {
      this.invitesReady = true;
      this.groupInvitesSubject.next(result?.data || []);
    });
  }

  getInvitesByGroup(groupId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient-invites/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadInvitesByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient-invites/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadEmailsByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/email-transactions/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addInvite(inviteData: any, consentOnly: boolean, consentList?: any[]): Observable<any> {
    inviteData.groupId = this.groupId;
    inviteData.consentOnly = consentOnly;
    inviteData.consentList = consentList;
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/patient-invites/consent`, inviteData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateInvite(id: string, inviteData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/patient-invites/${id}`, inviteData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updatePatientReferralSourceByInviteToken(inviteToken: string, referralData: { whereDidYouHearAboutUs: string }): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/patients/referral-source-from-invite`, {
      inviteToken,
      ...referralData
    });
  }
  
  resendInvite(id: string, email?: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/patient-invites/resend/${id}`, { email }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      catchError((err) => {
        if (err.status === 429 && err.error?.nextAllowedResendAt) {
          return throwError(() => ({
            ...err,
            nextAllowedResendAt: err.error.nextAllowedResendAt as string,
            message: err.error.message || 'Resend is on cooldown. Try again later.',
          }));
        }
        return throwError(() => err);
      })
    );
  }

  getInviteById(id: string | null): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/patient-invites/${id}`);
  }

  validateInvite(token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/patient-invites/validate/${token}`);
  }

  revokeInvite(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/patient-invites/revoke/${id}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Practice documents riding this invite — `tasks/practice-documents.md` §19
   *
   * BOTH ARE UNAUTHENTICATED, exactly like `getInviteById` and `validateInvite` above: the actor
   * is a patient with no account, and attaching `localStorage.getItem('token')` to a call made
   * from a link opened on a shared phone would leak a session into it. The invite's short token is
   * the whole of the authentication.
   *
   * AND IT TRAVELS IN THE BODY, NEVER IN THE URL — for both of them, POST for both of them. A
   * secret in a path lands in server logs, in the browser's history and in any `Referer` the page
   * emits; `submit()`'s own `formData.append('shortToken', …)` is the precedent this follows.
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Mint a signed download URL for one document's blank.
   *
   * Called again on the server's own cadence (`expiresInMinutes`) while the step is on screen, so
   * a patient who walks to a printer and comes back forty minutes later is not holding a dead URL.
   */
  getDocumentBlankUrl(inviteId: string, requestId: string, shortToken: string): Observable<InviteDocumentBlank> {
    return this.http
      .post<{ success: boolean; data: InviteDocumentBlank }>(
        `${this.apiUrl}/patient-invites/${encodeURIComponent(inviteId)}/documents/${encodeURIComponent(requestId)}/blank`,
        { shortToken })
      .pipe(map((response: any) => {
        const data = (response && response.data) || {};
        return {
          url: data.url || null,
          expiresInMinutes: typeof data.expiresInMinutes === 'number' ? data.expiresInMinutes : null,
          fileName: data.fileName || null,
          pageCount: typeof data.pageCount === 'number' ? data.pageCount : null,
        };
      }));
  }

  /**
   * WHAT THEY SENT LAST TIME, on a document the practice sent back.
   *
   * Short-lived read links behind the invite token, for the reason the rejection email does NOT
   * attach these pages: sending somebody's completed clinical form back out over unencrypted
   * email is a different risk from sending them a blank, and they are already opening an
   * authenticated link in order to resubmit.
   *
   * Answers an EMPTY LIST rather than an error on a document that was never sent back, so the
   * caller can ask whenever a step says it is a resubmission.
   */
  getDocumentPreviousFiles(inviteId: string, requestId: string, shortToken: string)
    : Observable<InviteDocumentPrevious> {
    return this.http
      .post<{ success: boolean; data: InviteDocumentPrevious }>(
        `${this.apiUrl}/patient-invites/${encodeURIComponent(inviteId)}`
        + `/documents/${encodeURIComponent(requestId)}/previous`,
        { shortToken })
      .pipe(map((response: any) => {
        const data = (response && response.data) || {};
        return {
          files: Array.isArray(data.files) ? data.files : [],
          expiresInMinutes: typeof data.expiresInMinutes === 'number' ? data.expiresInMinutes : null,
        };
      }));
  }

  /**
   * Return the completed pages for one document. SEVERAL FILES — the realistic return is a
   * photograph of each page (§5).
   *
   * `reportProgress` is on and that is not decoration: a ten-page scan is megabytes on a phone
   * connection, and an upload with no progress is one a patient abandons and re-sends. No
   * `Content-Type` is set — the browser writes the multipart boundary, and setting that header by
   * hand is the standard way to break a multipart upload.
   *
   * OUT OF BAND, on the step, rather than folded into the invite `PUT`: `submit()` builds one
   * `FormData` carrying `data` + `signature`, and pushing tens of megabytes of photographs through
   * the submission that finishes the invite would make the last button in the wizard the one most
   * likely to time out.
   */
  uploadDocumentFiles(
    inviteId: string,
    requestId: string,
    shortToken: string,
    files: File[],
  ): Observable<InviteDocumentUploadEvent> {
    const body = new FormData();
    body.append('shortToken', shortToken);
    for (const file of files) body.append('files', file, file.name);

    const url = `${this.apiUrl}/patient-invites/${encodeURIComponent(inviteId)}/documents/${encodeURIComponent(requestId)}/files`;

    // A returned document's pages feed the AI read, so they are rendered sharper than a note's.
    return withPdfPages$(body, { dpi: 200 }).pipe(
      switchMap((form) => this.http.request<InviteDocumentUploadResult>(
        new HttpRequest('POST', url, form, { reportProgress: true }))),
      map((event): InviteDocumentUploadEvent => {
        if (event.type === HttpEventType.UploadProgress) {
          const percent = event.total
            ? Math.min(100, Math.round((event.loaded / event.total) * 100))
            : null;
          return { kind: 'progress', percent };
        }
        if (event.type === HttpEventType.Response) {
          return { kind: 'done', result: (event.body || { success: true }) };
        }
        // Sent / ResponseHeader / DownloadProgress — the bytes are moving, nothing is known yet.
        return { kind: 'progress', percent: null };
      }),
    );
  }

  downloadConsentPDF(consentForm: any): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/consent/download/${consentForm.id}`, {
      responseType: 'blob' // Specify that the response is a Blob
    });
  }
}